import { 
  IWalletAdapter,
  WalletInfo,
  WalletConnectConfig,
  ConnectOptions,
  TransactionRequest,
  ChainConfig
} from './core/types';
import { WebAdapter } from './adapters/WebAdapter';
import { CapacitorAdapter } from './adapters/CapacitorAdapter';
import { SUPPORTED_CHAINS, BNB_MAINNET } from './core/chains';
import { WalletConnectionError } from './core/errors';
import { EventEmitter } from 'events';

export class WalletConnectBNB extends EventEmitter {
  private adapter: IWalletAdapter | null = null;
  private config: WalletConnectConfig;
  private isCapacitorEnvironment: boolean = false;

  constructor(config?: WalletConnectConfig) {
    super();
    
    this.config = {
      chains: config?.chains || [BNB_MAINNET],
      defaultChain: config?.defaultChain || '0x38', // BNB Mainnet
      projectId: config?.projectId || '',
      metadata: config?.metadata || {
        name: 'BNB DApp',
        description: 'BNB Chain DApp',
        url: 'https://example.com',
        icons: ['https://example.com/icon.png']
      },
      autoConnect: config?.autoConnect || false,
    };

    this.detectEnvironment();
    this.initializeAdapter();

    if (this.config.autoConnect) {
      this.autoConnect();
    }
  }

  private detectEnvironment(): void {
    // Check if running in Capacitor environment
    if (typeof window !== 'undefined') {
      this.isCapacitorEnvironment = !!(window as any).Capacitor;
    }
  }

  private initializeAdapter(): void {
    if (this.isCapacitorEnvironment) {
      if (!this.config.projectId) {
        console.warn('WalletConnect projectId is required for mobile apps. Get one at https://cloud.walletconnect.com');
      }
      this.adapter = new CapacitorAdapter(
        this.config.projectId || '',
        this.config.metadata
      );
    } else {
      this.adapter = new WebAdapter();
    }

    // Forward adapter events
    if (this.adapter) {
      this.adapter.onAccountsChanged((accounts) => {
        this.emit('accountsChanged', accounts);
      });

      this.adapter.onChainChanged((chainId) => {
        this.emit('chainChanged', chainId);
      });

      this.adapter.onDisconnect(() => {
        this.emit('disconnect');
      });
    }
  }

  /**
   * Connect to a wallet
   */
  async connect(options?: ConnectOptions): Promise<WalletInfo> {
    if (!this.adapter) {
      throw new WalletConnectionError('Adapter not initialized');
    }

    const connectOptions: ConnectOptions = {
      chainId: options?.chainId || this.config.defaultChain,
      walletType: options?.walletType,
      projectId: options?.projectId || this.config.projectId,
      showQrModal: options?.showQrModal !== false,
      qrModalOptions: options?.qrModalOptions,
    };

    try {
      const walletInfo = await this.adapter.connect(connectOptions);
      this.emit('connect', walletInfo);
      return walletInfo;
    } catch (error) {
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * Disconnect the wallet
   */
  async disconnect(): Promise<void> {
    if (this.adapter) {
      await this.adapter.disconnect();
      this.emit('disconnect');
    }
  }

  /**
   * Get connected account address
   */
  async getAccount(): Promise<string | null> {
    if (!this.adapter) return null;
    return this.adapter.getAccount();
  }

  /**
   * Get current chain ID
   */
  async getChainId(): Promise<string | null> {
    if (!this.adapter) return null;
    return this.adapter.getChainId();
  }

  /**
   * Check if wallet is connected
   */
  isConnected(): boolean {
    return this.adapter?.isConnected() || false;
  }

  /**
   * Switch to a different chain
   */
  async switchChain(chainId: string): Promise<void> {
    if (!this.adapter) {
      throw new WalletConnectionError('No wallet connected');
    }
    await this.adapter.switchChain(chainId);
    this.emit('chainChanged', chainId);
  }

  /**
   * Send a transaction
   */
  async sendTransaction(tx: TransactionRequest): Promise<string> {
    if (!this.adapter) {
      throw new WalletConnectionError('No wallet connected');
    }
    
    try {
      const txHash = await this.adapter.sendTransaction(tx);
      this.emit('transactionSent', txHash);
      return txHash;
    } catch (error) {
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * Sign a message
   */
  async signMessage(message: string): Promise<string> {
    if (!this.adapter) {
      throw new WalletConnectionError('No wallet connected');
    }
    
    try {
      const signature = await this.adapter.signMessage(message);
      this.emit('messageSigned', signature);
      return signature;
    } catch (error) {
      this.emit('error', error);
      throw error;
    }
  }

  /**
   * Auto connect to previously connected wallet
   */
  private async autoConnect(): Promise<void> {
    try {
      // Check if there's a cached session
      const cached = this.getCachedSession();
      if (cached) {
        await this.connect();
      }
    } catch (error) {
      console.error('Auto-connect failed:', error);
    }
  }

  /**
   * Get cached wallet session (if any)
   */
  private getCachedSession(): any {
    if (typeof window === 'undefined') return null;
    
    try {
      const cached = localStorage.getItem('wallet_connect_session');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }

  /**
   * Get supported chains
   */
  getSupportedChains(): Record<string, ChainConfig> {
    return SUPPORTED_CHAINS;
  }

  /**
   * Get environment info
   */
  getEnvironment(): { isCapacitor: boolean; isWeb: boolean } {
    return {
      isCapacitor: this.isCapacitorEnvironment,
      isWeb: !this.isCapacitorEnvironment,
    };
  }
}